// src/pages/Sales/ProductSearchPanel.jsx
import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

function formatPrice(v) {
  const n = Number(v || 0);
  return `${n.toLocaleString("vi-VN")}₫`;
}

export default function ProductSearchPanel({
  t,
  searchTerm,
  setSearchTerm,
  filteredProducts,
  onSelectProduct,
  loading = false,
  error = "",
}) {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  // Thumb hợp lý hơn cho POS (180px quá to)
  const THUMB = smDown ? 56 : mdDown ? 64 : 72;

  const count = filteredProducts?.length || 0;

  // Skeleton count theo view
  const skeletonCount = smDown ? 6 : 8;

  const headerTitle = useMemo(() => {
    // fallback nếu i18n chưa có key
    try {
      return t?.("Search Products") || "Tìm sản phẩm";
    } catch {
      return "Tìm sản phẩm";
    }
  }, [t]);

  const placeholder = useMemo(() => {
    try {
      return t?.("Search by name or SKU...") || "Tìm theo tên hoặc SKU...";
    } catch {
      return "Tìm theo tên hoặc SKU...";
    }
  }, [t]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Sticky header: title + search */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={900}>
            {headerTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {loading ? "Đang tải..." : error ? "Có lỗi" : `Kết quả: ${count}`}
          </Typography>
        </Box>

        <TextField
          fullWidth
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <Tooltip title="Xoá tìm kiếm">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            mt: 1,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />

        <Divider sx={{ mt: 1.25 }} />
      </Box>

      {/* Error */}
      {!!error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* List area */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pt: 1 }}>
        {/* Loading skeleton */}
        {loading ? (
          <Grid container spacing={1.25}>
            {Array.from({ length: skeletonCount }).map((_, i) => (
              <Grid item xs={12} key={i}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ display: "flex", alignItems: "center", p: 1.25, gap: 1.25 }}>
                    <Skeleton variant="rounded" width={THUMB} height={THUMB} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Skeleton width="62%" />
                      <Skeleton width="38%" />
                      <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
                        <Skeleton variant="rounded" width={90} height={24} />
                        <Skeleton variant="rounded" width={110} height={24} />
                      </Box>
                    </Box>
                    <Skeleton variant="circular" width={32} height={32} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : count === 0 ? (
          // Empty state
          <Box sx={{ textAlign: "center", py: 6 }}>
            <SearchIcon sx={{ fontSize: 46, color: "text.secondary", mb: 1 }} />
            <Typography fontWeight={800}>Không tìm thấy sản phẩm</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Thử đổi từ khoá, hoặc tìm theo SKU.
            </Typography>
          </Box>
        ) : (
          // List sản phẩm
          <Grid container spacing={1.25}>
            {filteredProducts.map((product) => {
              const outOfStock = !product.stock || product.stock <= 0;

              return (
                <Grid item xs={12} key={product.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      cursor: outOfStock ? "default" : "pointer",
                      opacity: outOfStock ? 0.72 : 1,
                      transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                      "&:hover": !outOfStock
                        ? {
                            transform: "translateY(-1px)",
                            boxShadow: 2,
                            borderColor: "primary.main",
                          }
                        : undefined,
                    }}
                    onClick={() => {
                      if (!outOfStock && onSelectProduct) onSelectProduct(product);
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.25,
                        gap: 1.25,
                      }}
                    >
                      <Avatar
                        src={product.image}
                        variant="rounded"
                        sx={{
                          width: THUMB,
                          height: THUMB,
                          borderRadius: 2,
                          bgcolor: "grey.100",
                          flexShrink: 0,
                        }}
                        imgProps={{ loading: "lazy" }}
                      >
                        {product.name?.charAt(0)}
                      </Avatar>

                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={900} noWrap>
                          {product.name}
                        </Typography>

                        <Typography variant="caption" color="text.secondary" noWrap>
                          SKU: {product.sku || "-"}
                        </Typography>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                          <Chip
                            label={formatPrice(product.price)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />

                          <Chip
                            label={`Tồn: ${product.stock || 0}`}
                            size="small"
                            color={outOfStock ? "default" : "success"}
                            variant={outOfStock ? "outlined" : "filled"}
                          />

                          {outOfStock && (
                            <Chip label="Hết hàng" size="small" variant="outlined" />
                          )}
                        </Box>
                      </Box>

                      <Tooltip title={outOfStock ? "Hết hàng" : "Thêm vào giỏ"}>
                        <span>
                          <IconButton
                            color="primary"
                            disabled={outOfStock}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!outOfStock && onSelectProduct) onSelectProduct(product);
                            }}
                            sx={{
                              border: (t) => `1px solid ${t.palette.divider}`,
                              borderRadius: 2,
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
